import { useState } from "react";
import { Trash2, AlertTriangle, CheckCircle2, Loader2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { useRemovePaymentMethod, useWorkspaceBilling, useCanManageBilling, isIndiaCard } from "@/hooks/use-workspace-billing";
import { useWorkspace } from "@/contexts/WorkspaceContext";

interface PaymentMethodCardProps {
  paymentMethodAdded: boolean;
  onRemoved?: () => void;
}

export function PaymentMethodCard({ paymentMethodAdded, onRemoved }: PaymentMethodCardProps) {
  const [isOpen, setIsOpen] = useState(false);
  const { currentWorkspaceId } = useWorkspace();
  const { data: billing } = useWorkspaceBilling();
  const removePaymentMethod = useRemovePaymentMethod();
  const canManage = useCanManageBilling();

  const isIndia = isIndiaCard(billing);
  const brand = billing?.payment_method_brand;
  const cardSummary = [
    brand ? brand.charAt(0).toUpperCase() + brand.slice(1) : null,
    isIndia ? "issued in India" : null,
  ].filter(Boolean).join(" · ");

  const handleRemove = async () => {
    try {
      await removePaymentMethod.mutateAsync({ workspace_id: currentWorkspaceId });
      setIsOpen(false);
      onRemoved?.();
    } catch (error) {
      // Kept open so the server's reason (e.g. SUBSCRIPTION_ACTIVE) stays visible.
    }
  };

  const openChange = (next: boolean) => {
    if (next) removePaymentMethod.reset();
    setIsOpen(next);
  };

  if (!paymentMethodAdded) {
    return null;
  }

  return (
    <div className="p-4 bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800/50 rounded-xl">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="w-10 h-10 rounded-xl bg-emerald-100 dark:bg-emerald-900/50 flex items-center justify-center">
            <CheckCircle2 className="w-5 h-5 text-emerald-600 dark:text-emerald-400" />
          </div>
          <div>
            <p className="font-medium text-emerald-800 dark:text-emerald-200">
              Payment method added
            </p>
            <p className="text-sm text-emerald-600 dark:text-emerald-400">
              {cardSummary || "Your workspace is ready for transactions"}
            </p>
          </div>
        </div>

        {canManage && (
        <AlertDialog open={isOpen} onOpenChange={openChange}>
          <AlertDialogTrigger asChild>
            <Button
              variant="ghost"
              size="sm"
              className="text-destructive hover:text-destructive hover:bg-destructive/10"
            >
              <Trash2 className="w-4 h-4 mr-2" />
              Remove
            </Button>
          </AlertDialogTrigger>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-destructive" />
                Remove Payment Method
              </AlertDialogTitle>
              <AlertDialogDescription asChild>
                <div className="space-y-3">
                  <p>
                    Are you sure you want to remove your payment method? This action will:
                  </p>
                  <ul className="list-disc pl-5 space-y-1 text-sm">
                    <li>Remove your saved card from this workspace</li>
                    <li>Disable auto-reload to prevent failed charge attempts</li>
                    <li>Require adding a new payment method for future top-ups</li>
                    {isIndia && (
                      <li>
                        Removing your card also removes its bank authorisation; you'll verify again
                        when you add a card.
                      </li>
                    )}
                  </ul>
                  <p className="font-medium text-foreground">
                    No future charges can occur from this payment method after removal.
                  </p>
                  <p>
                    If this workspace has an active Pro subscription, cancel it first —
                    the card can't be removed while the subscription is billing.
                  </p>
                </div>
              </AlertDialogDescription>
            </AlertDialogHeader>
            {removePaymentMethod.error && (
              <p className="text-sm font-medium text-destructive">
                {removePaymentMethod.error.message}
              </p>
            )}
            <AlertDialogFooter>
              <AlertDialogCancel disabled={removePaymentMethod.isPending}>
                Cancel
              </AlertDialogCancel>
              <AlertDialogAction
                // An action closes its dialog by default; this one stays open until the
                // server answers, so the busy state and a refusal are seen.
                onClick={(event) => {
                  event.preventDefault();
                  void handleRemove();
                }}
                disabled={removePaymentMethod.isPending}
                className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              >
                {removePaymentMethod.isPending && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {removePaymentMethod.isPending ? "Removing..." : "Remove Payment Method"}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
        )}
      </div>
    </div>
  );
}
